import { useState, useCallback } from 'react';
import { platformApi } from '@/services/platformApi';
import { nfcBridgeService } from '@/services/nfcBridgeService';
import type { CreateTapSessionRequest } from '@/types/prepaidTapSession';

export type PrepaidTapToPayPhase = 'idle' | 'creating_session' | 'native_tap' | 'success' | 'error';

export interface PrepaidTapToPayParams extends CreateTapSessionRequest {
  /** Required for native-credentials fetch inside the shell */
  accessToken?: string | null;
}

/**
 * Orchestrates POST /prepaid-card/tap-session then window.VaiuNFC.startTapToPay (NFC shell).
 */
export function usePrepaidTapToPay() {
  const [phase, setPhase] = useState<PrepaidTapToPayPhase>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase('idle');
    setMessage(null);
  }, []);

  const startTapToPay = useCallback(async (params: PrepaidTapToPayParams) => {
    setPhase('creating_session');
    setMessage(null);
    const accessToken =
      params.accessToken ?? platformApi.getToken();

    if (!accessToken) {
      setPhase('error');
      setMessage('Sign in required for tap to pay.');
      return { success: false as const, error: 'not_authenticated' };
    }

    try {
      const { tapSessionId } = await platformApi.prepaidCards.createTapSession({
        tokenKey: params.tokenKey,
        uniqueUserId: params.uniqueUserId,
        tokenId: params.tokenId,
        amount: params.amount,
        currency: params.currency,
        merchantContext: params.merchantContext,
      });

      setPhase('native_tap');
      const nativeResult = await nfcBridgeService.startTapToPay({
        tapSessionId,
        accessToken,
      });

      if (nativeResult.success) {
        setPhase('success');
        setMessage('Tap session completed in the NFC app.');
        return { success: true as const, tapSessionId };
      }

      setPhase('error');
      const err = nativeResult.error || 'native_tap_failed';
      setMessage(err);
      return { success: false as const, error: err, tapSessionId };
    } catch (e: unknown) {
      const err = e instanceof Error ? e.message : 'tap_session_failed';
      setPhase('error');
      setMessage(err);
      return { success: false as const, error: err };
    }
  }, []);

  return {
    phase,
    message,
    reset,
    startTapToPay,
    isBusy: phase === 'creating_session' || phase === 'native_tap',
  };
}
